import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { initMetaPixel, trackAddToCart } from '../utils/metaPixel';
import { CheckCircle2, AlertCircle, ArrowRight, Heart, ShieldCheck, XCircle, Activity, Footprints, Armchair } from 'lucide-react';
export function SalesPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  useEffect(() => {
    initMetaPixel();
  }, []);
  const handleBuyClick = () => {
    trackAddToCart();
    window.open('https://simplementemejor.com/cart/51631227339032:1', '_blank');
  };
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-stone-50 font-sans text-slate-800 selection:bg-teal-100" data-id="element-14">
      {/* Navigation / Header */}
      <nav className="w-full bg-white border-b border-stone-100 py-4 px-4 sticky top-0 z-50 shadow-sm" data-id="element-15">
        <div className="max-w-5xl mx-auto flex justify-between items-center" data-id="element-16">
          <span className="font-bold text-xl text-teal-700 tracking-tight" data-id="element-17">
            Camina Sin Dolor
          </span>
          <Button variant="primary" size="default" onClick={scrollToPricing} className="hidden sm:inline-flex" data-id="element-18">
            Obtener Guía
          </Button>
        </div>
      </nav>

      {/* HERO SECTION */}
      <header className="relative bg-white overflow-hidden" data-id="element-19">
        <div className="max-w-5xl mx-auto px-4 py-16 sm:py-24 lg:py-32" data-id="element-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-21">
            <div className="space-y-8" data-id="element-22">
              <div className="inline-block px-4 py-1.5 rounded-full bg-teal-50 text-teal-800 font-medium text-sm mb-2 border border-teal-100" data-id="element-23">
                Guía Especializada 65+
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] tracking-tight" data-id="element-24">
                Los primeros 10 pasos no deberían doler.
              </h1>
              <p className="text-xl sm:text-2xl text-slate-600 leading-relaxed font-medium" data-id="element-25">
                14 días. 10 minutos. Menos castigo en tus rodillas.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4" data-id="element-26">
                <Button size="xl" onClick={scrollToPricing} className="w-full shadow-teal-200/50" data-id="element-27">
                  Quiero el plan de 14 días — $6.39
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-28" />
                </Button>
              </div>
              <p className="text-sm text-slate-500 flex items-center gap-2" data-id="element-29">
                <ShieldCheck className="w-4 h-4 text-teal-600" data-id="element-30" />
                Compra segura y descarga inmediata
              </p>
            </div>
            <div className="relative mx-auto lg:ml-auto w-full max-w-md lg:max-w-full" data-id="element-31">
              <div className="relative rounded-2xl bg-stone-100 p-4 sm:p-8 shadow-xl ring-1 ring-stone-900/5" data-id="element-32">
                <img src="https://cdn.magicpatterns.com/uploads/sMZAkXNaN6yeWZNAbwkt8L/ebook_65_2B.png" alt="Portada del libro digital Camina Sin Dolor" className="w-full h-auto rounded-lg shadow-sm transform transition-transform hover:scale-[1.02] duration-500" data-id="element-33" />
              </div>
              {/* Decorative blob */}
              <div className="absolute -top-12 -right-12 w-64 h-64 bg-teal-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 -z-10 animate-pulse" data-id="element-34"></div>
              <div className="absolute -bottom-12 -left-12 w-64 h-64 bg-orange-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 -z-10" data-id="element-35"></div>
            </div>
          </div>
        </div>
      </header>

      {/* OPENING LEAD - Emotional Story */}
      <section className="py-20 bg-stone-50" data-id="element-36">
        <div className="max-w-3xl mx-auto px-4" data-id="element-37">
          <div className="prose prose-lg prose-slate mx-auto" data-id="element-38">
            <p className="text-2xl font-serif text-slate-800 leading-relaxed italic border-l-4 border-teal-500 pl-6 py-2 bg-white rounded-r-lg shadow-sm" data-id="element-39">
              "Hay un momento que casi nadie ve. No es cuando estás 'haciendo
              ejercicio'. Es cuando te levantas… y das{' '}
              <strong data-id="element-40">los primeros 10 pasos</strong>."
            </p>

            <div className="mt-10 space-y-6 text-lg text-slate-700 leading-relaxed" data-id="element-41">
              <p data-id="element-42">
                Ahí es cuando tu cuerpo decide si hoy vas a caminar normal… o si
                vas a caminar con esa negociación silenciosa:{' '}
                <em data-id="element-43">“despacio… no me vayas a doler.”</em>
              </p>
              <p data-id="element-44">Y lo peor no es el dolor.</p>
              <p data-id="element-45">
                Lo peor es lo que empieza a pasar después: sales menos, te
                tardas más, evitas escaleras, evitas tiendas grandes… y tu mundo
                se hace más pequeño.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-20 bg-white" data-id="element-46">
        <div className="max-w-4xl mx-auto px-4" data-id="element-47">
          <div className="text-center mb-16" data-id="element-48">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6" data-id="element-49">
              El problema real{' '}
              <span className="text-teal-600" data-id="element-50">(y por qué no es tu culpa)</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto" data-id="element-51">
              Tu rodilla y tu cadera no viven en un vacío. Si el tobillo está
              rígido o la cadera no estabiliza, la rodilla paga el precio.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16" data-id="element-52">
            <div className="bg-stone-50 p-8 rounded-2xl border border-stone-100" data-id="element-53">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-6" data-id="element-54">
                <Activity className="w-6 h-6 text-red-600" data-id="element-55" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-56">
                Estirar no basta
              </h3>
              <p className="text-slate-600" data-id="element-57">
                Se siente bien… pero si vuelves a caminar igual, vuelve el
                problema. Necesitas corregir la marcha.
              </p>
            </div>
            <div className="bg-stone-50 p-8 rounded-2xl border border-stone-100" data-id="element-58">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-6" data-id="element-59">
                <AlertCircle className="w-6 h-6 text-red-600" data-id="element-60" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-61">
                Rutinas intensas
              </h3>
              <p className="text-slate-600" data-id="element-62">
                Funcionan para otros cuerpos; a ti te dejan inflamada o
                asustada. Necesitas suavidad.
              </p>
            </div>
            <div className="bg-stone-50 p-8 rounded-2xl border border-stone-100" data-id="element-63">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-6" data-id="element-64">
                <Footprints className="w-6 h-6 text-red-600" data-id="element-65" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-66">
                Solo "caminar más"
              </h3>
              <p className="text-slate-600" data-id="element-67">
                Sin preparar la articulación es como manejar con una llanta
                baja: avanzas, pero dañas el resto.
              </p>
            </div>
          </div>

          <div className="bg-teal-50 rounded-2xl p-8 sm:p-12 text-center" data-id="element-68">
            <h3 className="text-2xl font-bold text-teal-900 mb-4" data-id="element-69">
              No necesitas "ser más fuerte" de golpe.
            </h3>
            <p className="text-xl text-teal-800" data-id="element-70">
              Necesitas una rutina que tu cuerpo acepte sin pelear.
            </p>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="py-20 bg-stone-100" data-id="element-71">
        <div className="max-w-5xl mx-auto px-4" data-id="element-72">
          <div className="text-center mb-16" data-id="element-73">
            <span className="text-teal-600 font-bold tracking-wider uppercase text-sm" data-id="element-74">
              El Método
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-6" data-id="element-75">
              Lo que vas a hacer{' '}
              <span className="font-normal text-slate-600" data-id="element-76">
                (sin equipo raro)
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto" data-id="element-77">
              Un plan claro de 14 días diseñado específicamente para desbloquear
              rigidez y corregir la carga.
            </p>
          </div>

          <div className="space-y-8" data-id="element-78">
            {/* Pillar 1 */}
            <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm flex flex-col md:flex-row gap-8 items-start md:items-center" data-id="element-79">
              <div className="bg-teal-100 rounded-full p-6 shrink-0" data-id="element-80">
                <Activity className="w-10 h-10 text-teal-700" data-id="element-81" />
              </div>
              <div data-id="element-82">
                <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-83">
                  1. Micro‑movilidad (10 min)
                </h3>
                <p className="text-lg text-slate-600" data-id="element-84">
                  Movimientos suaves para bajar rigidez sin provocarte ese
                  "dolor de mañana siguiente". Prepara tus articulaciones antes
                  de exigirles.
                </p>
              </div>
            </div>

            {/* Pillar 2 */}
            <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm flex flex-col md:flex-row gap-8 items-start md:items-center" data-id="element-85">
              <div className="bg-orange-100 rounded-full p-6 shrink-0" data-id="element-86">
                <Footprints className="w-10 h-10 text-orange-700" data-id="element-87" />
              </div>
              <div data-id="element-88">
                <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-89">
                  2. Reinicio de Marcha
                </h3>
                <p className="text-lg text-slate-600" data-id="element-90">
                  5 ajustes simples para que tus pasos dejen de castigar
                  rodillas. Aprende a distribuir el peso correctamente en cada
                  paso.
                </p>
              </div>
            </div>

            {/* Pillar 3 */}
            <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm flex flex-col md:flex-row gap-8 items-start md:items-center" data-id="element-91">
              <div className="bg-blue-100 rounded-full p-6 shrink-0" data-id="element-92">
                <Armchair className="w-10 h-10 text-blue-700" data-id="element-93" />
              </div>
              <div data-id="element-94">
                <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-95">
                  3. Banda + Silla
                </h3>
                <p className="text-lg text-slate-600" data-id="element-96">
                  Ejercicios seguros para activar cadera y estabilidad sin miedo
                  a caerte. Fortalece sin impacto.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-20 bg-white" data-id="element-97">
        <div className="max-w-4xl mx-auto px-4" data-id="element-98">
          <div className="bg-slate-900 rounded-3xl p-8 sm:p-16 text-white relative overflow-hidden" data-id="element-99">
            {/* Background pattern */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-teal-900/30 rounded-full blur-3xl -mr-16 -mt-16" data-id="element-100"></div>

            <h2 className="text-3xl font-bold mb-10 relative z-10" data-id="element-101">
              Qué recibes al comprar:
            </h2>

            <ul className="space-y-6 relative z-10" data-id="element-102">
              {['PDF Completo "Camina Sin Dolor (65+)" (plan 14 días)', 'Rutina base de 10 minutos (micro‑movilidad)', 'Guía de Reinicio de marcha (2–5 minutos)', 'Protocolos especiales para mañanas, escaleras y estar sentada', 'Tabla de referencia: "si te duele aquí, empieza con esto"'].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-103">
                  <CheckCircle2 className="w-6 h-6 text-teal-400 shrink-0 mt-0.5" data-id="element-104" />
                  <span className="text-lg text-slate-200" data-id="element-105">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* WHO IS THIS FOR */}
      <section className="py-20 bg-stone-50" data-id="element-106">
        <div className="max-w-5xl mx-auto px-4" data-id="element-107">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-108">
            <div data-id="element-109">
              <h3 className="text-2xl font-bold text-teal-800 mb-6 flex items-center gap-2" data-id="element-110">
                <Heart className="w-6 h-6" data-id="element-111" />
                ¿Para quién es?
              </h3>
              <ul className="space-y-4" data-id="element-112">
                <li className="flex gap-3" data-id="element-113">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-2.5 shrink-0" data-id="element-114"></div>
                  <p className="text-lg text-slate-700" data-id="element-115">
                    Mujer 65+ que quiere caminar mejor{' '}
                    <strong data-id="element-116">sin gimnasio</strong>
                  </p>
                </li>
                <li className="flex gap-3" data-id="element-117">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-2.5 shrink-0" data-id="element-118"></div>
                  <p className="text-lg text-slate-700" data-id="element-119">
                    Con dolor/rigidez moderado–alto, que necesita un plan suave
                    y claro
                  </p>
                </li>
                <li className="flex gap-3" data-id="element-120">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-2.5 shrink-0" data-id="element-121"></div>
                  <p className="text-lg text-slate-700" data-id="element-122">
                    Que quiere recuperar independencia sin sentirse "vieja" ni
                    "incapaz"
                  </p>
                </li>
              </ul>
            </div>

            <div data-id="element-123">
              <h3 className="text-2xl font-bold text-slate-700 mb-6 flex items-center gap-2" data-id="element-124">
                <XCircle className="w-6 h-6" data-id="element-125" />
                ¿Para quién NO es?
              </h3>
              <div className="bg-white p-6 rounded-xl border border-stone-200" data-id="element-126">
                <p className="text-lg text-slate-600" data-id="element-127">
                  Si tienes <strong data-id="element-128">dolor agudo reciente</strong>, caída fuerte,
                  cirugía reciente o señales graves: por favor busca primero una
                  evaluación médica presencial.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING / CTA */}
      <section id="pricing" className="py-24 bg-white" data-id="element-129">
        <div className="max-w-3xl mx-auto px-4 text-center" data-id="element-130">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" data-id="element-131">
            Empieza a caminar mejor hoy mismo
          </h2>
          <p className="text-xl text-slate-600 mb-12" data-id="element-132">
            No te prometo magia. Te prometo un proceso que respeta tu dolor.
          </p>

          <div className="bg-stone-50 rounded-3xl p-8 sm:p-12 border border-stone-200 shadow-xl relative" data-id="element-133">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-teal-600 text-white px-6 py-2 rounded-full font-bold text-sm tracking-wide shadow-md" data-id="element-134">
              OFERTA ESPECIAL
            </div>

            <div className="mb-8" data-id="element-135">
              <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-136">
                <span className="text-2xl text-slate-400 line-through" data-id="element-137">
                  $19
                </span>
                <span className="text-6xl font-bold text-teal-600" data-id="element-138">$6.39</span>
                <span className="text-slate-500 text-xl" data-id="element-139">USD</span>
              </div>
              <div className="mt-3" data-id="element-140">
                <span className="inline-block bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-semibold" data-id="element-141">
                  ¡Ahorra 66%!
                </span>
              </div>
            </div>

            <p className="text-slate-600 mb-8 text-lg" data-id="element-142">
              Menos que una ida a la farmacia… y puede cambiar cómo se sienten
              tus pasos esta misma semana.
            </p>

            <div className="mb-8 mx-auto max-w-xs" data-id="element-143">
              <div className="relative rounded-2xl bg-stone-100 p-4 shadow-xl ring-1 ring-stone-900/5" data-id="element-144">
                <img src="https://cdn.magicpatterns.com/uploads/sMZAkXNaN6yeWZNAbwkt8L/ebook_65_2B.png" alt="Portada del libro digital Camina Sin Dolor" className="w-full h-auto rounded-lg shadow-sm" data-id="element-145" />
              </div>
            </div>

            <Button size="xl" onClick={handleBuyClick} className="w-full mb-6 text-lg shadow-teal-200/50" data-id="element-146">
              Quiero el plan de 14 días — $6.39
              <ArrowRight className="ml-2 w-5 h-5" data-id="element-147" />
            </Button>

            <div className="flex justify-center gap-6 text-sm text-slate-500" data-id="element-148">
              <span className="flex items-center gap-1" data-id="element-149">
                <ShieldCheck className="w-4 h-4" data-id="element-150" /> Pago Seguro
              </span>
              <span className="flex items-center gap-1" data-id="element-151">
                <CheckCircle2 className="w-4 h-4" data-id="element-152" /> Acceso Inmediato
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-stone-100 py-12 border-t border-stone-200" data-id="element-153">
        <div className="max-w-5xl mx-auto px-4 text-center text-slate-500" data-id="element-154">
          <p className="mb-4 font-semibold text-slate-700" data-id="element-155">
            Camina Sin Dolor (65+)
          </p>
          <p className="text-sm mb-6 max-w-2xl mx-auto" data-id="element-156">
            Este producto es una guía educativa y no sustituye el consejo médico
            profesional. Siempre consulta a tu médico antes de comenzar
            cualquier rutina de ejercicios.
          </p>

          {/* Legal Links */}
          <div className="flex justify-center gap-6 mb-6" data-id="element-157">
            <button onClick={() => setPrivacyOpen(true)} className="text-sm text-slate-600 hover:text-teal-600 underline underline-offset-2 transition-colors" data-id="element-158">
              Política de Privacidad
            </button>
            <button onClick={() => setTermsOpen(true)} className="text-sm text-slate-600 hover:text-teal-600 underline underline-offset-2 transition-colors" data-id="element-159">
              Términos y Condiciones
            </button>
          </div>

          <div className="text-sm" data-id="element-160">
            &copy; {new Date().getFullYear()} Camina Sin Dolor. Todos los
            derechos reservados.
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <Modal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" data-id="element-161">
        <div className="space-y-4 text-base" data-id="element-162">
          <p className="text-slate-600 text-sm" data-id="element-163">
            Última actualización:{' '}
            {new Date().toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>

          <h3 className="font-bold text-lg text-slate-900" data-id="element-164">
            1. Información que Recopilamos
          </h3>
          <p data-id="element-165">
            Recopilamos información personal que nos proporcionas
            voluntariamente al realizar una compra, incluyendo tu nombre,
            dirección de correo electrónico y datos de pago procesados de forma
            segura a través de nuestros proveedores de pago.
          </p>

          <h3 className="font-bold text-lg text-slate-900" data-id="element-166">
            2. Uso de la Información
          </h3>
          <p data-id="element-167">Utilizamos tu información para:</p>
          <ul className="list-disc pl-6 space-y-1" data-id="element-168">
            <li data-id="element-169">Procesar y entregar tu compra</li>
            <li data-id="element-170">Enviarte el producto digital adquirido</li>
            <li data-id="element-171">Comunicarnos contigo sobre tu pedido</li>
            <li data-id="element-172">Mejorar nuestros productos y servicios</li>
          </ul>

          <h3 className="font-bold text-lg text-slate-900" data-id="element-173">
            3. Protección de Datos
          </h3>
          <p data-id="element-174">
            Implementamos medidas de seguridad técnicas y organizativas para
            proteger tu información personal contra acceso no autorizado,
            pérdida o alteración.
          </p>

          <h3 className="font-bold text-lg text-slate-900" data-id="element-175">
            4. Compartir Información
          </h3>
          <p data-id="element-176">
            No vendemos ni compartimos tu información personal con terceros,
            excepto cuando sea necesario para procesar tu pago o cumplir con
            obligaciones legales.
          </p>

          <h3 className="font-bold text-lg text-slate-900" data-id="element-177">5. Tus Derechos</h3>
          <p data-id="element-178">
            Tienes derecho a acceder, corregir o eliminar tu información
            personal. Para ejercer estos derechos, puedes contactarnos a través
            de los canales indicados.
          </p>

          <h3 className="font-bold text-lg text-slate-900" data-id="element-179">6. Cookies</h3>
          <p data-id="element-180">
            Utilizamos cookies esenciales para el funcionamiento del sitio y
            cookies analíticas para entender cómo los visitantes interactúan con
            nuestra página.
          </p>
        </div>
      </Modal>

      {/* Terms and Conditions Modal */}
      <Modal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos y Condiciones" data-id="element-181">
        <div className="space-y-4 text-base" data-id="element-182">
          <p className="text-slate-600 text-sm" data-id="element-183">
            Última actualización:{' '}
            {new Date().toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>

          <h3 className="font-bold text-lg text-slate-900" data-id="element-184">
            1. Aceptación de los Términos
          </h3>
          <p data-id="element-185">
            Al acceder y utilizar este sitio web y realizar una compra, aceptas
            estar sujeto a estos términos y condiciones de uso.
          </p>

          <h3 className="font-bold text-lg text-slate-900" data-id="element-186">
            2. Descripción del Producto
          </h3>
          <p data-id="element-187">
            "Camina Sin Dolor (65+)" es una guía digital en formato PDF que
            contiene información educativa sobre ejercicios y rutinas de
            movilidad. Este producto es únicamente informativo y educativo.
          </p>

          <h3 className="font-bold text-lg text-slate-900" data-id="element-188">
            3. Aviso Médico Importante
          </h3>
          <p data-id="element-189">
            Este producto{' '}
            <strong data-id="element-190">NO sustituye el consejo médico profesional</strong>. Antes
            de comenzar cualquier programa de ejercicios, consulta con tu médico
            o profesional de salud, especialmente si tienes condiciones médicas
            preexistentes.
          </p>

          <h3 className="font-bold text-lg text-slate-900" data-id="element-191">
            4. Entrega del Producto
          </h3>
          <p data-id="element-192">
            Una vez completada la compra, recibirás acceso inmediato al producto
            digital a través del correo electrónico proporcionado durante la
            compra.
          </p>

          <h3 className="font-bold text-lg text-slate-900" data-id="element-193">
            5. Política de Reembolso
          </h3>
          <p data-id="element-194">
            Debido a la naturaleza digital del producto, no ofrecemos reembolsos
            una vez que el producto ha sido entregado y descargado. Si tienes
            problemas técnicos para acceder al producto, contáctanos y te
            ayudaremos.
          </p>

          <h3 className="font-bold text-lg text-slate-900" data-id="element-195">
            6. Propiedad Intelectual
          </h3>
          <p data-id="element-196">
            Todo el contenido de este producto está protegido por derechos de
            autor. No está permitida la reproducción, distribución o reventa del
            material sin autorización expresa.
          </p>

          <h3 className="font-bold text-lg text-slate-900" data-id="element-197">
            7. Limitación de Responsabilidad
          </h3>
          <p data-id="element-198">
            No nos hacemos responsables de lesiones o daños que puedan resultar
            del uso de la información contenida en este producto. El usuario
            asume toda la responsabilidad por el uso del contenido.
          </p>

          <h3 className="font-bold text-lg text-slate-900" data-id="element-199">
            8. Modificaciones
          </h3>
          <p data-id="element-200">
            Nos reservamos el derecho de modificar estos términos en cualquier
            momento. Las modificaciones entrarán en vigor inmediatamente después
            de su publicación en el sitio.
          </p>
        </div>
      </Modal>
    </div>;
}